import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tag, Loader2, Users, TrendingUp, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { publicApi } from '../../utils/api';

const PRESETS = [[10, 15], [15, 20], [20, 30]];

/** The merchant's member offer: one Pro % and a bigger Elite %. Shown first on the dashboard. */
export function MemberOfferCard({ api, info, onSaved }) {
  const [form, setForm] = useState({ pro: '', elite: '', headline: '', terms: '' });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const hasOffer = (info?.pro_discount_pct || 0) > 0;

  useEffect(() => {
    setForm({ pro: info?.pro_discount_pct ?? '', elite: info?.elite_discount_pct ?? '', headline: info?.offer_headline || '', terms: info?.offer_terms || '' });
  }, [info]);
  useEffect(() => { publicApi.get('/api/merchant-partners/stats/public').then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/merchant/profile', {
        pro_discount_pct: form.pro, elite_discount_pct: form.elite || form.pro,
        offer_headline: form.headline, offer_terms: form.terms,
      });
      toast.success('Member offer saved — it now shows on your Marketplace card');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save offer'); }
    setSaving(false);
  };

  return (
    <Card className={`mb-6 border ${hasOffer ? 'bg-[#041A3B] border-white/10' : 'bg-gradient-to-r from-[#00E676]/10 to-[#041A3B] border-[#00E676]/40'}`} data-testid="merchant-offer-card">
      <CardContent className="p-5 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 justify-between">
          <div>
            <h3 className="font-outfit text-lg text-white font-semibold flex items-center gap-2"><Tag className="w-5 h-5 text-[#00E676]" />Your member offer</h3>
            <p className="text-sm text-[#A0B4CC] mt-1">
              {hasOffer ? 'This is the discount AGA members see on your Marketplace card.' : 'Set one simple discount. Merchants with an offer are shown first in the Marketplace.'}
            </p>
          </div>
          {stats && (
            <div className="flex gap-3 text-xs text-[#A0B4CC]" data-testid="merchant-offer-reach">
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[#00E5FF]" />{stats.members.toLocaleString()} members can see it</span>
              {stats.avg_elite_pct > 0 && <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-[#FFD740]" />avg {stats.avg_pro_pct}% / {stats.avg_elite_pct}%</span>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(([p, e]) => (
            <button key={p} type="button" onClick={() => setForm({ ...form, pro: p, elite: e })}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${Number(form.pro) === p && Number(form.elite) === e ? 'bg-[#00E676] text-[#000B18] border-[#00E676]' : 'border-white/15 text-[#A0B4CC] hover:border-white/30'}`}
              data-testid={`merchant-offer-preset-${p}`}>Pro {p}% · Elite {e}%</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-[#00E5FF] text-xs">Pro discount %</Label>
            <Input type="number" min="0" max="90" value={form.pro} onChange={(e) => setForm({ ...form, pro: e.target.value })} className="bg-[#000B18] border-white/10 text-white" data-testid="merchant-offer-pro-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#FFD740] text-xs flex items-center gap-1"><Crown className="w-3 h-3" />Elite discount %</Label>
            <Input type="number" min="0" max="90" value={form.elite} onChange={(e) => setForm({ ...form, elite: e.target.value })} className="bg-[#000B18] border-white/10 text-white" data-testid="merchant-offer-elite-input" />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-[#A0B4CC] text-xs">Headline (optional)</Label>
            <Input maxLength={80} placeholder="e.g. 20% off all dine-in orders" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="bg-[#000B18] border-white/10 text-white" data-testid="merchant-offer-headline-input" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="space-y-1 flex-1">
            <Label className="text-[#A0B4CC] text-xs">Terms (optional)</Label>
            <Input maxLength={300} placeholder="e.g. Not valid with other promos. Show AGA card QR." value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="bg-[#000B18] border-white/10 text-white" data-testid="merchant-offer-terms-input" />
          </div>
          <Button onClick={save} disabled={saving || !form.pro} className="bg-[#00E676] text-[#000B18] hover:bg-[#5BFFA0]" data-testid="merchant-offer-save-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}{hasOffer ? 'Update offer' : 'Publish offer'}
          </Button>
        </div>
        <p className="text-[11px] text-[#5C7599]">You only give the discount when an AGA member pays. When you scan a member's QR and enter the bill, the right discount is applied for their tier automatically.</p>
      </CardContent>
    </Card>
  );
}
